import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Collapse,
  TablePagination,
} from '@mui/material';
import {
  Email,
  Phone,
  Cancel,
  Download,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
// @ts-ignore
import * as XLSX from 'xlsx';

interface ShuttleBus {
  id: string;
  name: string;
  capacity: number;
}

interface ShuttleSchedule {
  id: string;
  departure_date: string;
  departure_time: string;
  pickup_location: string;
  destination: string;
  available_seats: number;
  price: number | null;
  status: string;
}

interface Booking {
  id: string;
  schedule_id: string;
  guest_email: string;
  guest_name: string;
  guest_nickname: string | null;
  card_id: string | null;
  seats_booked: number;
  booking_code: string;
  phone: string | null;
  notes: string | null;
  status: string;
  confirmation_sent_at: string | null;
  created_at: string;
}

interface ShuttleBookingsListProps {
  shuttle: ShuttleBus;
  scheduleId?: string | null;
  onExport?: (bookings: Booking[]) => void;
}

const ShuttleBookingsList: React.FC<ShuttleBookingsListProps> = ({ shuttle, scheduleId, onExport }) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<ShuttleSchedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<string>('');
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedBooking, setExpandedBooking] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Cancellation dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState<Booking | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shuttles/${shuttle.id}/schedules`);
      const scheduleData = response.data.filter((s: ShuttleSchedule) => s.status !== 'cancelled');
      setSchedules(scheduleData);
      
      // If scheduleId is provided and exists in the list, use it
      if (scheduleId && scheduleData.some((s: ShuttleSchedule) => s.id === scheduleId)) {
        setSelectedSchedule(scheduleId);
      } else if (scheduleData.length > 0) {
        setSelectedSchedule(scheduleData[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
      setError(t('shuttle.admin.errors.fetchSchedules'));
    } finally {
      setLoading(false);
    }
  }, [shuttle.id, scheduleId, t]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/schedules/${selectedSchedule}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      setError(t('shuttle.admin.errors.fetchBookings'));
    } finally {
      setLoading(false);
    }
  }, [selectedSchedule, t]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  useEffect(() => {
    // If scheduleId is provided, use it directly
    if (scheduleId) {
      setSelectedSchedule(scheduleId);
    }
  }, [scheduleId]);

  useEffect(() => {
    if (selectedSchedule) {
      fetchBookings();
    }
  }, [selectedSchedule, fetchBookings]);

  const handleCancelBooking = async () => {
    if (!cancellingBooking) return;

    try {
      // In a real implementation, you might want to add an admin endpoint for cancellation
      // For now, we'll just update the booking status
      await api.put(`/bookings/${cancellingBooking.id}/cancel`, {
        reason: cancellationReason,
      });
      
      setCancelDialogOpen(false);
      setCancellingBooking(null);
      setCancellationReason('');
      fetchBookings();
    } catch (error: any) {
      console.error('Failed to cancel booking:', error);
      setError(error.response?.data?.detail || t('shuttle.admin.errors.cancelBooking'));
    }
  };

  const handleExportToExcel = () => {
    const schedule = schedules.find(s => s.id === selectedSchedule);
    if (!schedule) return;

    const exportData = bookings.map(booking => ({
      [t('shuttle.admin.bookingCode')]: booking.booking_code,
      [t('shuttle.admin.name')]: booking.guest_name,
      [t('shuttle.admin.nickname')]: booking.guest_nickname || '-',
      [t('shuttle.admin.email')]: booking.guest_email,
      [t('shuttle.admin.phone')]: booking.phone || '-',
      [t('shuttle.admin.seats')]: booking.seats_booked,
      [t('shuttle.admin.status')]: t(`shuttle.booking.${booking.status}`),
      [t('shuttle.admin.bookedAt')]: format(new Date(booking.created_at), 'yyyy-MM-dd HH:mm'),
      [t('shuttle.admin.notes')]: booking.notes || '-',
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bookings');

    const fileName = `${shuttle.name}_${format(new Date(schedule.departure_date), 'yyyy-MM-dd')}_${schedule.departure_time.substring(0, 5)}.xlsx`;
    XLSX.writeFile(wb, fileName);

    onExport?.(bookings);
  };

  const getSelectedScheduleDetails = () => {
    return schedules.find(s => s.id === selectedSchedule);
  };

  const schedule = getSelectedScheduleDetails();
  const totalSeatsBooked = bookings.reduce((sum, booking) => 
    booking.status === 'confirmed' ? sum + booking.seats_booked : sum, 0
  );

  if (loading && schedules.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (schedules.length === 0) {
    return (
      <Alert severity="info">
        {t('shuttle.admin.noSchedulesForBookings')}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>{t('shuttle.admin.selectSchedule')}</InputLabel>
          <Select
            value={selectedSchedule}
            onChange={(e) => setSelectedSchedule(e.target.value)}
            label={t('shuttle.admin.selectSchedule')}
          >
            {schedules.map((schedule) => (
              <MenuItem key={schedule.id} value={schedule.id}>
                {format(new Date(schedule.departure_date), 'EEE, MMM d')} - {schedule.departure_time.substring(0, 5)} | 
                {schedule.pickup_location} → {schedule.destination}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {bookings.length > 0 && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExportToExcel}
            color="success"
          >
            {t('shuttle.admin.exportToExcel')}
          </Button>
        )}
      </Box>

      {schedule && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="subtitle1" gutterBottom>
            {t('shuttle.admin.scheduleDetails')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Chip
              label={`${totalSeatsBooked}/${schedule.available_seats} ${t('shuttle.admin.seatsBooked')}`}
              color={totalSeatsBooked >= schedule.available_seats ? 'error' : 'success'}
            />
            <Typography variant="body2">
              <strong>{t('shuttle.admin.price')}:</strong> {schedule.price ? `€${schedule.price}` : t('shuttle.admin.free')}
            </Typography>
            <Typography variant="body2">
              <strong>{t('shuttle.admin.totalRevenue')}:</strong> 
              €{schedule.price ? (totalSeatsBooked * schedule.price).toFixed(2) : '0.00'}
            </Typography>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : bookings.length === 0 ? (
        <Alert severity="info">
          {t('shuttle.admin.noBookings')}
        </Alert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{t('shuttle.admin.bookingCode')}</TableCell>
                  <TableCell>{t('shuttle.admin.passenger')}</TableCell>
                  <TableCell>{t('shuttle.admin.contact')}</TableCell>
                  <TableCell align="center">{t('shuttle.admin.seats')}</TableCell>
                  <TableCell>{t('shuttle.admin.status')}</TableCell>
                  <TableCell>{t('shuttle.admin.bookedAt')}</TableCell>
                  <TableCell align="center">{t('shuttle.admin.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((booking) => (
                    <React.Fragment key={booking.id}>
                      <TableRow>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => setExpandedBooking(
                              expandedBooking === booking.id ? null : booking.id
                            )}
                          >
                            {expandedBooking === booking.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {booking.booking_code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {booking.guest_name}
                            </Typography>
                            {booking.guest_nickname && (
                              <Typography variant="caption" color="text.secondary">
                                ({booking.guest_nickname})
                              </Typography>
                            )}
                            {booking.card_id && (
                              <Chip
                                label={t('shuttle.passengers.member')}
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Email fontSize="small" color="action" />
                              <Typography variant="caption">
                                {booking.guest_email}
                              </Typography>
                            </Box>
                            {booking.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Phone fontSize="small" color="action" />
                                <Typography variant="caption">
                                  {booking.phone}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={booking.seats_booked}
                            size="small"
                            color="default"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`shuttle.booking.${booking.status}`)}
                            size="small"
                            color={booking.status === 'confirmed' ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {format(new Date(booking.created_at), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {booking.status === 'confirmed' && (
                            <Tooltip title={t('shuttle.admin.cancelBooking')}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setCancellingBooking(booking);
                                  setCancelDialogOpen(true);
                                }}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={expandedBooking === booking.id} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              {booking.notes && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {t('shuttle.admin.notes')}:
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {booking.notes}
                                  </Typography>
                                </Box>
                              )}
                              {booking.confirmation_sent_at && (
                                <Typography variant="caption" color="text.secondary">
                                  {t('shuttle.admin.confirmationSent')}: {format(new Date(booking.confirmation_sent_at), 'dd/MM/yyyy HH:mm')}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={bookings.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}

      {/* Cancel Booking Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('shuttle.admin.cancelBookingTitle')}
        </DialogTitle>
        <DialogContent>
          {cancellingBooking && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{t('shuttle.admin.passenger')}:</strong> {cancellingBooking.guest_name}
              </Typography>
              <Typography variant="body2">
                <strong>{t('shuttle.admin.bookingCode')}:</strong> {cancellingBooking.booking_code}
              </Typography>
              <Typography variant="body2">
                <strong>{t('shuttle.admin.seats')}:</strong> {cancellingBooking.seats_booked}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('shuttle.admin.cancellationReason')}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder={t('shuttle.admin.cancellationReasonPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleCancelBooking}
            variant="contained"
            color="error"
          >
            {t('shuttle.admin.confirmCancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShuttleBookingsList;